/**
 * Supported UI languages. Arabic and English are complete and required for every feature (Operating Rule 7).
 * Hindi, Malayalam, Tamil, Urdu and Tagalog serve the GCC expat workforce; missing keys fall back to English.
 */
export const LOCALES = ['en', 'ar', 'hi', 'ml', 'ta', 'ur', 'tl'] as const;
export type Locale = (typeof LOCALES)[number];

export interface LocaleMeta {
  code: Locale;
  /** Endonym shown in the language picker. */
  nativeName: string;
  dir: 'ltr' | 'rtl';
  /** Native digit system for the optional "native numerals" setting. */
  nativeDigits: 'latn' | 'arab' | 'arabext' | 'deva' | 'mlym' | 'tamldec';
  complete: boolean;
}

export const LOCALE_META: Record<Locale, LocaleMeta> = {
  en: { code: 'en', nativeName: 'English', dir: 'ltr', nativeDigits: 'latn', complete: true },
  ar: { code: 'ar', nativeName: 'العربية', dir: 'rtl', nativeDigits: 'arab', complete: true },
  hi: { code: 'hi', nativeName: 'हिन्दी', dir: 'ltr', nativeDigits: 'deva', complete: false },
  ml: { code: 'ml', nativeName: 'മലയാളം', dir: 'ltr', nativeDigits: 'mlym', complete: false },
  ta: { code: 'ta', nativeName: 'தமிழ்', dir: 'ltr', nativeDigits: 'tamldec', complete: false },
  ur: { code: 'ur', nativeName: 'اردو', dir: 'rtl', nativeDigits: 'arabext', complete: false },
  tl: { code: 'tl', nativeName: 'Tagalog', dir: 'ltr', nativeDigits: 'latn', complete: false },
};

export const isLocale = (v: unknown): v is Locale =>
  typeof v === 'string' && (LOCALES as readonly string[]).includes(v);
export const dirOf = (l: Locale) => LOCALE_META[l].dir;
